/**
 * Replays a variant's change records against the live page.
 *
 * This is the half of the editor contract that runs in production: the extension
 * authors records, this applies them. **An unrecognised key is ignored**, so a
 * record shape shipped by the editor before the engine understands it does nothing
 * at all — no error, the edit just never happens for real visitors. That is why the
 * engine deploys first.
 *
 * `applyGeneration` guards against a stale async apply finishing after a newer one
 * started, which would otherwise resurrect changes from a superseded variant on
 * single-page-app navigations.
 */
(function (window, document) {
  const Utils = window.MVTUtils;
  let appliedChanges = [];
  let appliedVariantIds = [];

  let applyGeneration = 0;

  /**
   * Resolves the node a text edit should actually target.
   *
   * A numeric `depth` walks that many steps from the element (negative walks up);
   * anything else means "descend through single-child wrappers that carry the same
   * text". Without this, editing a heading wrapped in a `<span>` would replace the
   * wrapper and destroy the inner markup.
   */
  function findTextLeaf(element, depth) {
    const steps = depth === "" || depth === null || depth === undefined ? NaN : Number(depth);

    if (Number.isInteger(steps)) {
      return walkFromTarget(element, steps);
    }

    let node = element;
    while (
      node.children &&
      node.children.length === 1 &&
      node.textContent === node.children[0].textContent
    ) {
      node = node.children[0];
    }
    return node;
  }

  function walkFromTarget(element, steps) {
    let node = element;

    for (let i = 0; i < Math.abs(steps); i++) {
      const next =
        steps > 0
          ? node.children && node.children.length === 1
            ? node.children[0]
            : null
          : node.parentElement;

      if (!next || (steps < 0 && (next === document.body || next === document.documentElement))) {
        return null;
      }

      node = next;
    }

    return node;
  }

  function applyLeafHTML(element, html, depth) {
    const leaf = findTextLeaf(element, depth);
    if (leaf && leaf.innerHTML !== html) {
      leaf.innerHTML = html;
    }
  }

  const EDITABLE_ATTRS = ["alt", "loading", "type", "name", "placeholder", "required"];

  function applyEditableAttrs(element, attrs) {
    if (typeof attrs === "string") {
      try {
        attrs = JSON.parse(attrs);
      } catch {
        return;
      }
    }

    if (!element || !attrs || typeof attrs !== "object") {
      return;
    }
    for (const name of Object.keys(attrs)) {
      if (EDITABLE_ATTRS.indexOf(name) === -1) {
        continue;
      }
      const value = attrs[name];

      if (value === "" || value === null || value === undefined) {
        if (element.hasAttribute(name)) {
          element.removeAttribute(name);
        }
      } else if (element.getAttribute(name) !== value) {
        element.setAttribute(name, value);
      }
    }
  }

  const canonicalOuterHTML = new Map();
  function desiredOuterHTML(html) {
    let canonical = canonicalOuterHTML.get(html);
    if (canonical === undefined) {
      try {
        const template = document.createElement("template");
        template.innerHTML = html;
        canonical = template.innerHTML;
      } catch (e) {
        canonical = html;
      }
      canonicalOuterHTML.set(html, canonical);
    }
    return canonical;
  }

  const PER_CHANGE_SELECTOR_MS = 2500;
  function selectorTimeout() {
    const deadline = window.__MVT_APPLY_DEADLINE__;
    if (!Number.isFinite(deadline)) return PER_CHANGE_SELECTOR_MS;
    const remaining = deadline - Date.now();
    if (remaining <= 0) return 0;
    return Math.min(PER_CHANGE_SELECTOR_MS, remaining);
  }

  const APPLICATION = {
    cleanupAppliedVariants: function () {
      applyGeneration++;

      appliedVariantIds.forEach((variantId) => {
        const styleId = `mvt-custom-css-${variantId}`;
        const styleElement = document.getElementById(styleId);
        if (styleElement) {
          styleElement.remove();
        }

        const scriptId = `mvt-custom-js-${variantId}`;
        const scriptElement = document.getElementById(scriptId);
        if (scriptElement) {
          scriptElement.remove();
        }
      });

      appliedChanges = [];
      appliedVariantIds = [];
    },

    executeChange: async function (variant) {
      try {
        const myGeneration = applyGeneration;
        const isStale = () => myGeneration !== applyGeneration;
        const variantId = variant.id || variant.key;

        if (variantId && !appliedVariantIds.includes(variantId)) {
          appliedVariantIds.push(variantId);
        }

        if (variant.customCSS) {
          const styleId = `mvt-custom-css-${variantId}`;

          const existingStyle = document.getElementById(styleId);
          if (existingStyle) {
            existingStyle.remove();
          }
          const style = document.createElement("style");
          style.id = styleId;
          style.textContent = variant.customCSS;
          document.head.appendChild(style);
        }

        if (variant.customJS) {
          const scriptId = `mvt-custom-js-${variantId}`;

          const existingScript = document.getElementById(scriptId);
          if (existingScript) {
            existingScript.remove();
          }
          const script = document.createElement("script");
          script.id = scriptId;
          script.textContent = variant.customJS;
          document.head.appendChild(script);
        }

        if (variant.customHTML) {
        }

        const selectedVariationData = variant.data || [];

        for (let changeIndex = 0; changeIndex < selectedVariationData.length; changeIndex++) {
          let change = selectedVariationData[changeIndex];

          if (!change) {
            continue;
          }

          try {
            let changeSignature =
              changeIndex +
              ":" +
              (change.rm || change.target || change.oriEl || "") +
              ":" +
              (change.refEl || "") +
              ":" +
              (change.dir || "");

            if (!appliedChanges.includes(changeSignature) && "rm" in change) {
              const element = await Utils.waitForSelector(change.rm, {
                timeoutMs: selectorTimeout(),
                intervalMs: 50,
              });
              if (isStale()) return;
              if (element && element.parentNode) {
                element.parentNode.removeChild(element);
                appliedChanges.push(changeSignature);
              }
            } else if (
              !appliedChanges.includes(changeSignature) &&
              change.dir &&
              change.oriEl &&
              change.refEl
            ) {
              const originalElement = await Utils.waitForSelector(change.oriEl, {
                timeoutMs: selectorTimeout(),
                intervalMs: 50,
              });
              const referenceElement = await Utils.waitForSelector(change.refEl, {
                timeoutMs: selectorTimeout(),
                intervalMs: 50,
              });
              if (isStale()) return;

              if (originalElement && referenceElement && referenceElement.parentNode) {
                if ("a" === change.dir) {
                  if (referenceElement.nextSibling) {
                    referenceElement.parentNode.insertBefore(
                      originalElement,
                      referenceElement.nextSibling,
                    );
                  } else {
                    referenceElement.parentNode.appendChild(originalElement);
                  }
                  appliedChanges.push(changeSignature);
                } else if ("b" === change.dir) {
                  referenceElement.parentNode.insertBefore(originalElement, referenceElement);
                  appliedChanges.push(changeSignature);
                } else if ("p" === change.dir) {
                  referenceElement.insertAdjacentElement("afterbegin", originalElement);
                  appliedChanges.push(changeSignature);
                }
              }
            } else if (
              !appliedChanges.includes(changeSignature) &&
              change.dir &&
              change.refEl &&
              "htmlBlock" in change
            ) {
              const element = await Utils.waitForSelector(change.refEl, {
                timeoutMs: selectorTimeout(),
                intervalMs: 50,
              });
              if (isStale()) return;
              if (element) {
                var insertPosition =
                  "a" === change.dir
                    ? "afterend"
                    : "b" === change.dir
                      ? "beforebegin"
                      : "p" === change.dir
                        ? "afterbegin"
                        : "";
                element.insertAdjacentHTML(insertPosition, change.htmlBlock);
                appliedChanges.push(changeSignature);
              }
            } else if (change.target) {
              const targetElement = await Utils.waitForSelector(change.target, {
                timeoutMs: selectorTimeout(),
                intervalMs: 50,
              });
              if (isStale()) return;
              if (targetElement && targetElement instanceof HTMLElement) {
                const hasLeafHTML = "leafHTML" in change;
                let textProperties = hasLeafHTML
                  ? ["src", "href"]
                  : ["textContent", "innerText", "innerHTML", "src", "href"];
                let attributeProperties = ["style", "class"];

                if (hasLeafHTML) {
                  applyLeafHTML(targetElement, change.leafHTML, change.leafDepth);
                }

                if (change.attrs) {
                  applyEditableAttrs(targetElement, change.attrs);
                }

                for (const property of textProperties) {
                  if (!(property in change)) continue;
                  if ("src" === property && "IMG" === targetElement.tagName) {
                    await Utils.preloadImage(change[property], selectorTimeout());
                    if (isStale()) return;
                    targetElement.src = change[property];
                    if (targetElement.hasAttribute("srcset")) {
                      targetElement.removeAttribute("srcset");
                    }
                  } else {
                    targetElement[property] = change[property];
                  }
                }

                attributeProperties.forEach((property) => {
                  if (property in change) {
                    targetElement.setAttribute(property, change[property]);
                  }
                });

                if (
                  "outerHTML" in change &&
                  targetElement.outerHTML !== desiredOuterHTML(change.outerHTML)
                ) {
                  targetElement.outerHTML = change.outerHTML;
                }

                if (Utils && typeof Utils.unhideScopedSelector === "function") {
                  Utils.unhideScopedSelector(change.target);
                }

                if (changeIndex === selectedVariationData.length - 1) {
                  setTimeout(() => {
                    if (isStale()) return;
                    const refreshedElement = document.querySelector(change.target);
                    if (refreshedElement && refreshedElement instanceof HTMLElement) {
                      if (hasLeafHTML) {
                        applyLeafHTML(refreshedElement, change.leafHTML, change.leafDepth);
                      }

                      if (change.attrs) {
                        applyEditableAttrs(refreshedElement, change.attrs);
                      }

                      textProperties.forEach((property) => {
                        if (property in change && refreshedElement[property] !== change[property]) {
                          refreshedElement[property] = change[property];
                          if (
                            "src" === property &&
                            "IMG" === refreshedElement.tagName &&
                            refreshedElement.hasAttribute("srcset")
                          ) {
                            refreshedElement.removeAttribute("srcset");
                          }
                        }
                      });

                      attributeProperties.forEach((property) => {
                        if (
                          property in change &&
                          refreshedElement.getAttribute(property) !== change[property]
                        ) {
                          refreshedElement.setAttribute(property, change[property]);
                        }
                      });
                    }
                  }, 100);
                }
              }
            }
          } catch (error) {
            console.error(error);
          }
        }
      } catch (e) {
        console.error(
          "MVT Engine: Failed to apply variant:",
          variant && (variant.id || variant.key),
          e,
        );
      }
    },

    _variantStableId: function (variant) {
      if (!variant) return null;
      return variant.id || variant.key || null;
    },

    _assignmentStorageKey: function (campaignId, visitorId) {
      return `mvtVariantAssignment_${campaignId || "nocampaign"}_${visitorId || "novisitor"}`;
    },

    _readPersistedVariantId: function (campaignId, visitorId) {
      try {
        if (!window || !window.localStorage || !campaignId || !visitorId) return null;
        return (
          window.localStorage.getItem(this._assignmentStorageKey(campaignId, visitorId)) || null
        );
      } catch (e) {
        return null;
      }
    },

    _writePersistedVariantId: function (campaignId, visitorId, variantStableId) {
      try {
        if (!window || !window.localStorage || !campaignId || !visitorId || !variantStableId)
          return;

        const Utils = window.MVTUtils;
        if (Utils && Utils.consentBlocked && Utils.consentBlocked()) return;
        window.localStorage.setItem(
          this._assignmentStorageKey(campaignId, visitorId),
          variantStableId,
        );
      } catch (e) {}
    },

    selectVariant: function (changes, visitorId) {
      if (!Array.isArray(changes) || changes.length === 0) return null;

      const Utils = window.MVTUtils;

      const campaignId = (changes.find((c) => c && c.campaignId) || {}).campaignId || null;

      const persistedId = this._readPersistedVariantId(campaignId, visitorId);
      if (persistedId) {
        const persisted = changes.find(
          (c) => String(this._variantStableId(c)) === String(persistedId),
        );
        if (persisted) {
          return persisted;
        }
      }

      const ordered = changes.slice().sort((a, b) => {
        const x = String(this._variantStableId(a));
        const y = String(this._variantStableId(b));
        return x < y ? -1 : x > y ? 1 : 0;
      });

      const seed =
        Utils && typeof Utils.hashString === "function" ? Utils.hashString(visitorId) : 0;

      let totalWeight = 0;
      const weights = ordered.map((variant) => {
        const raw = variant && Number(variant.distributionPercentage);
        const weight = Number.isFinite(raw) && raw > 0 ? raw : 0;
        totalWeight += weight;
        return weight;
      });

      let selected = null;
      if (ordered.length > 0) {
        if (totalWeight <= 0) {
          selected = ordered[seed % ordered.length];
        } else {
          const SCALE = 100;
          const total = Math.max(1, Math.round(totalWeight * SCALE));
          let pick = seed % total;
          for (let i = 0; i < ordered.length; i++) {
            pick -= Math.round(weights[i] * SCALE);
            if (pick < 0) {
              selected = ordered[i];
              break;
            }
          }

          if (!selected) {
            for (let i = ordered.length - 1; i >= 0; i--) {
              if (weights[i] > 0) {
                selected = ordered[i];
                break;
              }
            }
          }
        }
      }
      selected = selected || ordered[0] || null;

      if (selected) {
        this._writePersistedVariantId(campaignId, visitorId, this._variantStableId(selected));
      }
      return selected;
    },

    applyVariant: async function (variant) {
      if (!variant) {
        console.warn("MVT Engine: applyVariant called with no variant");
        return null;
      }
      await this.executeChange(variant);
      return variant;
    },

    applyChanges: async function (changes, options) {
      if (!Array.isArray(changes) || changes.length === 0) {
        console.warn("MVT Engine: No variants available to apply");
        return null;
      }
      const selectedVariant =
        (options && options.selectedVariant) ||
        this.selectVariant(changes, options && options.visitorId);
      console.log("MVT Engine: Selected variant (deterministic)", {
        variantName: selectedVariant?.name,
        variantKey: selectedVariant?.key,
        dataCount: selectedVariant?.data?.length || 0,
        candidateCount: changes.length,
      });
      await this.executeChange(selectedVariant);
      return selectedVariant;
    },
  };

  window.MVTApplicationModule = APPLICATION;

  window.MVTResolveTextLeaf = findTextLeaf;
})(window, document);
